import { useEffect } from 'react'

/**
 * iOS / WhatsApp-style rubber-band overscroll for any scrollable container.
 *
 * The visual effect: when the user pulls past a boundary (or in any
 * direction in a non-scrollable list), the rows STRETCH APART — top rows
 * translate more than bottom rows on a top pull, so visible gaps grow
 * between items. Same trick WhatsApp uses on its chat list.
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null)
 *   useElasticOverscroll(ref, { rowSelector: 'a[href^="/chats/"]' })
 *   <div ref={ref} className="overflow-y-auto"> ... </div>
 *
 * If `rowSelector` is omitted we operate on the scroll container's direct
 * element children. If you have a sticky header among those children, pass
 * a more specific selector to skip it (sticky elements look broken when
 * translated).
 *
 * Damping ~2.6 matches iOS feel. Spring-back uses iOS's release curve
 * cubic-bezier(0.32, 0.72, 0, 1).
 *
 * Performance: the non-passive touchmove (the one that calls
 * preventDefault) is attached only for gestures that BEGIN at a scroll
 * boundary or in a non-scrollable container. Mid-list scrolls never pay
 * the JS-thread tax that a non-passive touchmove imposes on Android
 * Chrome — that tax was the dominant cause of janky fast-scroll. Trade-off:
 * a fling that hits the top mid-gesture won't rubber-band on the
 * continuation; the user has to lift and re-pull from the boundary. This
 * matches iOS's actual native feel.
 */
export function useElasticOverscroll(
  scrollRef: React.RefObject<HTMLElement | null>,
  opts: { rowSelector?: string; ignoreSelector?: string } = {},
) {
  const { rowSelector, ignoreSelector } = opts
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (typeof window === 'undefined') return
    // Touch-capable detection (foldables can report primary as 'fine'
    // because of stylus support, so use `any-pointer: coarse` plus legacy
    // fallbacks).
    const touchCapable =
      window.matchMedia('(any-pointer: coarse)').matches ||
      'ontouchstart' in window ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
    if (!touchCapable) return

    const DAMPING = 2.6
    const MAX_PULL = 60                     // total translate cap on top row
    const RELEASE_MS = 360
    const RELEASE_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'

    function rows(): HTMLElement[] {
      if (rowSelector) {
        return Array.from(
          el!.querySelectorAll<HTMLElement>(rowSelector),
        )
      }
      return Array.from(el!.children) as HTMLElement[]
    }

    let startY: number | null = null
    let pulling: 'top' | 'bottom' | null = null
    let frame = 0
    let touchMoveAttached = false

    function atTop(): boolean { return el!.scrollTop <= 0 }
    function atBottom(): boolean {
      return Math.ceil(el!.scrollTop + el!.clientHeight) >= el!.scrollHeight
    }
    function nonScrollable(): boolean {
      return el!.scrollHeight <= el!.clientHeight + 1
    }
    function ignoredTouchTarget(target: EventTarget | null): boolean {
      return Boolean(
        ignoreSelector &&
        target instanceof Element &&
        target.closest(ignoreSelector),
      )
    }

    function detachTouchMove() {
      if (touchMoveAttached) {
        el!.removeEventListener('touchmove', onTouchMove)
        touchMoveAttached = false
      }
    }

    function applyStretch(deltaY: number) {
      const sign = Math.sign(deltaY)
      const magnitude = Math.abs(deltaY) / DAMPING
      const capped = Math.min(magnitude, MAX_PULL + (magnitude - MAX_PULL) * 0.2)
      const pull = sign * Math.min(capped, MAX_PULL * 1.4)

      const list = rows()
      const N = list.length
      if (N === 0) return

      // Distribution function — top row gets the full pull, last row gets
      // ~0. Square-root makes the top items move noticeably MORE than the
      // middle (so the spread between rows is visible). Linear feels too
      // uniform and less "rubber-bandy".
      for (let i = 0; i < N; i++) {
        const t01 = pulling === 'top' ? (1 - i / N) : (i / N)
        // Square-root weighting so top/bottom rows clearly lead.
        const factor = Math.sqrt(Math.max(0, t01))
        const y = pull * factor
        const r = list[i]
        r.style.transition = 'none'
        r.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0)`
        r.style.willChange = 'transform'
      }
    }

    function reset(animate: boolean) {
      const list = rows()
      for (const r of list) {
        r.style.transition = animate
          ? `transform ${RELEASE_MS}ms ${RELEASE_EASING}`
          : 'none'
        r.style.transform = ''
      }
      // Clear willChange after the animation finishes so the GPU layer
      // can be released.
      window.setTimeout(() => {
        for (const r of list) r.style.willChange = ''
      }, animate ? RELEASE_MS + 50 : 0)
      pulling = null
      startY = null
    }

    function onTouchStart(ev: TouchEvent) {
      if (ev.touches.length !== 1) return
      if (ignoredTouchTarget(ev.target)) {
        startY = null
        pulling = null
        detachTouchMove()
        return
      }
      startY = ev.touches[0].clientY
      pulling = null
      // Attach the non-passive touchmove only when this gesture could
      // plausibly trigger rubber-band — i.e. we're already at a boundary
      // or the container can't scroll. Mid-list scrolls stay on the
      // browser's compositor thread with zero JS gating.
      if (nonScrollable() || atTop() || atBottom()) {
        el!.addEventListener('touchmove', onTouchMove, { passive: false })
        touchMoveAttached = true
      }
    }

    function onTouchMove(ev: TouchEvent) {
      if (startY === null) return
      if (ev.touches.length !== 1) {
        reset(true)
        return
      }
      const y = ev.touches[0].clientY
      const dy = y - startY
      if (pulling === null) {
        if (Math.abs(dy) < 4) return
        if (nonScrollable()) {
          pulling = dy > 0 ? 'top' : 'bottom'
        } else if (dy > 0 && atTop()) {
          pulling = 'top'
        } else if (dy < 0 && atBottom()) {
          pulling = 'bottom'
        } else {
          // First decisive move is INTO the list, not a boundary pull —
          // detach so the rest of the fling runs on the compositor
          // thread without JS gating.
          detachTouchMove()
          return
        }
      }
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => applyStretch(dy))
      ev.preventDefault()
    }

    function onTouchEnd() {
      cancelAnimationFrame(frame)
      if (pulling !== null) reset(true)
      else startY = null
      detachTouchMove()
    }

    function onTouchCancel() {
      cancelAnimationFrame(frame)
      reset(true)
      detachTouchMove()
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchCancel)
      detachTouchMove()
      cancelAnimationFrame(frame)
      // Clear any leftover transforms.
      for (const r of rows()) {
        r.style.transition = 'none'
        r.style.transform = ''
        r.style.willChange = ''
      }
    }
  }, [scrollRef, rowSelector, ignoreSelector])
}
